// Calls: shapes from the API and the helpers screens share.
import { api } from "./api";

export type CallMedia = "audio" | "video";
export type JoinInfo = { url: string; token: string; identity: string; room: string };
export type CallInfo = {
  id: string; media: CallMedia; status: "ringing" | "active" | "ended";
  endReason: "completed" | "missed" | "declined" | "cancelled" | "ended_by_host" | "failed" | "time_limit" | null;
  startedAt: string; answeredAt: string | null; endedAt: string | null;
  startedBy: { id: string; name: string } | null;
  conversation: { id: string; kind: "dm" | "group" | "channel" } | null;
  participants: { userId: string; identity: string; name: string; role: string; status: "ringing" | "declined" | "missed" | "joining" | "joined" | "left" | "removed"; handRaisedAt: string | null }[];
  me: string | null;
  recording: { id: string; startedAt: string; by: string | null } | null;
  transcribing: boolean;
};
export type Ringing = {
  callId: string; conversationId: string; media: CallMedia; caller: { id: string; name: string };
  conversationKind: "dm" | "group" | "channel"; conversationName: string | null; people: number; ringSeconds: number;
};
export type CallHistoryItem = {
  id: string; conversationId: string; title: string; available: boolean; media: CallMedia; status: CallInfo["status"];
  endReason: CallInfo["endReason"]; direction: "incoming" | "outgoing"; startedBy: { id: string; name: string } | null;
  myStatus: CallInfo["participants"][number]["status"]; startedAt: string; durationSeconds: number | null;
};

// Passed to the call screen when navigating, so it doesn't ask for a second token.
export type CallNavState = { join?: JoinInfo; call?: CallInfo; media?: CallMedia };

export async function startCall(slug: string, conversationId: string, media: CallMedia) {
  return api<{ call: CallInfo; join: JoinInfo; joinedExisting: boolean }>(`/orgs/${slug}/conversations/${conversationId}/calls`, { method: "POST", body: { media } });
}

export function formatDuration(seconds: number) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return h ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}` : `${m}:${String(s).padStart(2, "0")}`;
}

// A soft two-tone ring (or the caller's ring-back) made with Web Audio: no sound files to load. Browsers only allow
// sound after the person has interacted with the page; until then this stays silent.
export function startTone(kind: "incoming" | "ringback"): () => void {
  let ctx: AudioContext | null = null;
  try { ctx = new AudioContext(); } catch { return () => {}; }
  const audio = ctx;
  const gain = audio.createGain();
  gain.gain.value = 0;
  gain.connect(audio.destination);
  const tones = kind === "incoming" ? [440, 480] : [440, 480];
  const oscillators = tones.map((f) => { const o = audio.createOscillator(); o.frequency.value = f; o.connect(gain); o.start(); return o; });
  const level = kind === "incoming" ? 0.06 : 0.03;
  const pattern = () => {
    const t = audio.currentTime;
    gain.gain.setValueAtTime(level, t);
    gain.gain.setValueAtTime(0, t + (kind === "incoming" ? 1.2 : 2));
  };
  pattern();
  const timer = setInterval(pattern, kind === "incoming" ? 3000 : 6000);
  return () => {
    clearInterval(timer);
    for (const o of oscillators) { try { o.stop(); } catch { /* already stopped */ } }
    void audio.close().catch(() => {});
  };
}
